const STORAGE_PREFIX = 'sag:gated-content:';

const parseIntOr = (value, fallback) => {
  const parsed = Number.parseInt(String(value || ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const readLocalState = key => {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.unlocked !== true || !Number.isFinite(parsed.ts)) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeLocalState = (key, ts) => {
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + key,
      JSON.stringify({ unlocked: true, ts, version: 1 })
    );
  } catch (error) {
    // no-op
  }
};

const clearLocalState = key => {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + key);
  } catch (error) {
    // no-op
  }
};

const readCookieState = key => {
  const cookieName = `${encodeURIComponent(STORAGE_PREFIX + key)}=`;
  const matched = document.cookie.split('; ').find(cookie => cookie.indexOf(cookieName) === 0);
  if (!matched) {
    return null;
  }

  const value = matched.substring(cookieName.length);
  const ts = parseIntOr(decodeURIComponent(value), 0);
  if (!ts) {
    return null;
  }

  return { unlocked: true, ts, version: 1 };
};

const writeCookieState = (key, ts, days) => {
  const expires = new Date(Date.now() + days * 86400000);
  document.cookie = `${encodeURIComponent(STORAGE_PREFIX + key)}=${encodeURIComponent(
    String(ts)
  )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const clearCookieState = key => {
  document.cookie = `${encodeURIComponent(STORAGE_PREFIX + key)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const isStateValid = (state, days) => {
  if (!state || state.unlocked !== true || !Number.isFinite(state.ts)) {
    return false;
  }

  const maxAge = Math.max(1, days) * 86400000;
  return Date.now() - state.ts <= maxAge;
};

const getStoredState = (key, days) => {
  const localState = readLocalState(key);
  if (isStateValid(localState, days)) {
    return localState;
  }

  const cookieState = readCookieState(key);
  if (isStateValid(cookieState, days)) {
    return cookieState;
  }

  return null;
};

const persistState = (key, days) => {
  const ts = Date.now();
  writeLocalState(key, ts);
  writeCookieState(key, ts, days);
};

const clearPersistedState = key => {
  clearLocalState(key);
  clearCookieState(key);
};

const setUnlocked = (wrapper, unlocked, persist = false) => {
  const gate = wrapper.querySelector('[data-gated-content-gate]');
  const payload = wrapper.querySelector('[data-gated-content-payload]');

  wrapper.classList.toggle('is-unlocked', unlocked);

  if (gate) {
    gate.setAttribute('aria-hidden', unlocked ? 'true' : 'false');
  }
  if (payload) {
    payload.setAttribute('aria-hidden', unlocked ? 'false' : 'true');
  }

  if (unlocked && persist) {
    const key = wrapper.dataset.gatedKey || '';
    const days = parseIntOr(wrapper.dataset.gatedDays, 30);
    if (key) {
      persistState(key, days);
    }
  }
};

const bindDirectMarketoSuccess = (wrapper, expectedFormId) => {
  if (wrapper.dataset.gatedMktoBound === '1') {
    return;
  }

  const bind = () => {
    if (!window.MktoForms2 || typeof window.MktoForms2.whenReady !== 'function') {
      return false;
    }

    window.MktoForms2.whenReady(form => {
      const formId = Number.parseInt(
        typeof form.getId === 'function' ? String(form.getId()) : '',
        10
      );
      if (expectedFormId > 0 && formId !== expectedFormId) {
        return;
      }

      if (form.__sagGatedContentPatched) {
        return;
      }

      form.onSuccess(() => {
        setUnlocked(wrapper, true, true);
        return false;
      });
      form.__sagGatedContentPatched = true;
    });

    wrapper.dataset.gatedMktoBound = '1';
    return true;
  };

  if (bind()) {
    return;
  }

  let attempts = 0;
  const maxAttempts = 40;
  const poll = window.setInterval(() => {
    attempts += 1;
    if (bind() || attempts >= maxAttempts) {
      window.clearInterval(poll);
    }
  }, 100);
};

const bindVideoModal = wrapper => {
  if (wrapper.dataset.gatedModalBound === '1') {
    return;
  }

  const modal = wrapper.querySelector('[data-gated-video-modal]');
  const openButton = wrapper.querySelector('[data-gated-video-modal-open]');
  if (!modal || !openButton) {
    return;
  }

  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  const closeButtons = Array.from(modal.querySelectorAll('[data-gated-video-modal-close]'));
  const videoContainer = modal.querySelector('.o-gated-content__modal-video');

  const pauseIframePlayer = iframe => {
    if (!iframe || !iframe.contentWindow) {
      return;
    }
    const src = iframe.getAttribute('src') || '';
    try {
      if (src.indexOf('youtube.com') !== -1 || src.indexOf('youtube-nocookie.com') !== -1) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      } else if (src.indexOf('vimeo.com') !== -1) {
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    } catch (error) {
      // no-op
    }
  };

  const pauseVideo = () => {
    if (!videoContainer) {
      return;
    }
    videoContainer.querySelectorAll('iframe').forEach(pauseIframePlayer);
    // <wistia-player> exposes pause() once player.js has defined it.
    videoContainer.querySelectorAll('video, wistia-player').forEach(video => {
      try {
        if (typeof video.pause === 'function') {
          // VideoTracking.js reports this pause; it skips other script pauses.
          if (video.localName === 'wistia-player' && !video.paused) {
            video.dataset.videoPauseReason = 'modal-close';
          }
          video.pause();
        }
      } catch (error) {
        // no-op
      }
    });
  };

  const openModal = () => {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-gated-modal-open');
  };
  const closeModal = () => {
    pauseVideo();
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    if (!document.querySelector('.o-gated-content__modal.is-open')) {
      document.body.classList.remove('has-gated-modal-open');
    }
  };

  openButton.addEventListener('click', openModal);
  closeButtons.forEach(button => button.addEventListener('click', closeModal));
  window.addEventListener('keydown', event => {
    if (event.key === 'Escape' && modal.classList.contains('is-open')) {
      closeModal();
    }
  });

  wrapper.dataset.gatedModalBound = '1';
};

const bindResetButton = (wrapper, key) => {
  const resetButton = wrapper.querySelector('[data-gated-content-reset]');
  if (!resetButton || wrapper.dataset.gatedResetBound === '1') {
    return;
  }

  resetButton.addEventListener('click', () => {
    clearPersistedState(key);
    setUnlocked(wrapper, false, false);
  });

  wrapper.dataset.gatedResetBound = '1';
};

const GatedContent = () => {
  const blocks = Array.from(document.querySelectorAll('[data-gated-content]'));
  if (!blocks.length) {
    return;
  }
  const isAdminContext =
    document.body.classList.contains('wp-admin') ||
    document.body.classList.contains('block-editor-page');

  const params = new URLSearchParams(window.location.search);
  const consumedParamNames = new Set();

  blocks.forEach(wrapper => {
    const key = (wrapper.dataset.gatedKey || '').trim();
    if (!key) {
      return;
    }

    const days = parseIntOr(wrapper.dataset.gatedDays, 30);
    const successParam = (wrapper.dataset.gatedSuccessParam || 'gate_success').trim();
    const resetParam = (wrapper.dataset.gatedResetParam || 'gate_reset').trim();
    const successParamValue = params.get(successParam);
    const resetParamValue = params.get(resetParam);

    if (isAdminContext) {
      setUnlocked(wrapper, false, false);
    } else if (resetParamValue && (resetParamValue === key || resetParamValue === '1')) {
      clearPersistedState(key);
      setUnlocked(wrapper, false, false);
      consumedParamNames.add(resetParam);
    } else if (successParamValue && successParamValue === key) {
      setUnlocked(wrapper, true, true);
      consumedParamNames.add(successParam);
    } else {
      const state = getStoredState(key, days);
      setUnlocked(wrapper, !!state, false);
    }

    const eventName = (wrapper.dataset.gatedSuccessEvent || 'marketoForm:success').trim();
    const expectedFormId = parseIntOr(wrapper.dataset.gatedAjaxFormId, 0);

    if (eventName) {
      window.addEventListener(eventName, event => {
        if (expectedFormId > 0) {
          const detailFormId = event && event.detail ? parseIntOr(event.detail.formId, 0) : 0;
          if (detailFormId !== expectedFormId) {
            return;
          }
        }

        setUnlocked(wrapper, true, true);
      });
    }

    bindDirectMarketoSuccess(wrapper, expectedFormId);
    bindVideoModal(wrapper);
    bindResetButton(wrapper, key);
  });

  if (consumedParamNames.size > 0) {
    consumedParamNames.forEach(name => params.delete(name));
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }
};

export default GatedContent;
